import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarX, Check, DownloadSimple, MagnifyingGlass, Plus, Trash, UserCheck, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { daysAgoLocal, formatErr, fmtDate, inr, todayLocal } from "@/lib/api";

const leaveBadge = (status) => {
  const map = {
    approved: "text-status-green bg-status-green/10",
    rejected: "text-status-red bg-status-red/10",
    pending: "text-amber-600 bg-status-amber/20",
  };
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${map[status]}`} data-testid={`leave-status-${status}`}>{status}</span>;
};

const ReviewButtons = ({ onReview, id }) => (
  <div className="flex gap-2">
    <button data-testid={`approve-leave-${id}`} aria-label="Approve leave" onClick={() => onReview(id, "approved")} className="p-1.5 rounded-lg text-status-green hover:bg-status-green/10 transition-colors duration-200" title="Approve">
      <Check size={16} weight="bold" />
    </button>
    <button data-testid={`reject-leave-${id}`} aria-label="Reject leave" onClick={() => onReview(id, "rejected")} className="p-1.5 rounded-lg text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Reject">
      <X size={16} weight="bold" />
    </button>
  </div>
);

export function LeavesTab({ students = [] }) {
  const [section, setSection] = useState("teacher");
  const [teacherLeaves, setTeacherLeaves] = useState([]);
  const [studentLeaves, setStudentLeaves] = useState([]);
  const [cForm, setCForm] = useState({ student_id: "", from_date: todayLocal(), to_date: todayLocal(), reason: "" });
  const [creating, setCreating] = useState(false);
  const [selectedStudentLeaves, setSelectedStudentLeaves] = useState([]);
  const pendingStudentLeaves = studentLeaves.filter((l) => l.status === "pending");

  const submitStudentLeave = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/school/student-leaves", cForm);
      toast.success("Student leave recorded (approved)");
      setCForm({ student_id: "", from_date: todayLocal(), to_date: todayLocal(), reason: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCreating(false);
    }
  };

  const load = () => {
    api.get("/school/teacher-leaves").then((r) => setTeacherLeaves(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/school/student-leaves").then((r) => setStudentLeaves(r.data)).catch((e) => toast.error(formatErr(e)));
  };
  useEffect(load, []);

  const review = (kind) => async (id, status) => {
    try {
      await api.patch(`/school/${kind}-leaves/${id}`, { status });
      toast.success(`Leave ${status}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const bulkReviewStudents = async (status) => {
    if (!selectedStudentLeaves.length) return;
    try {
      await api.post("/school/student-leaves/bulk-review", { ids: selectedStudentLeaves, status });
      toast.success(`${selectedStudentLeaves.length} leave(s) ${status}`);
      setSelectedStudentLeaves([]);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="leaves-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-extrabold">Leave Applications</h1>
        <div className="flex gap-2">
          {[["teacher", "Teacher leaves"], ["student", "Student leaves"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`leaves-section-${id}`}
              onClick={() => setSection(id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors duration-200 ${section === id ? "bg-brand-coral text-white" : "bg-white border border-slate-200 text-slate-600"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {section === "teacher" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
              <tr><th className="px-5 py-3">Teacher</th><th className="px-5 py-3">Dates</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Reason</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody>
              {teacherLeaves.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`teacher-leave-${l.id}`}>
                  <td className="px-5 py-3.5 font-semibold">{l.teacher_name}</td>
                  <td className="px-5 py-3.5">{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</td>
                  <td className="px-5 py-3.5 capitalize">{l.leave_type}</td>
                  <td className="px-5 py-3.5 text-slate-600 max-w-xs truncate">{l.reason}</td>
                  <td className="px-5 py-3.5">{leaveBadge(l.status)}{l.reviewed_by && <span className="block text-xs text-slate-400 mt-1">Reviewed by {l.reviewed_by}</span>}</td>
                  <td className="px-5 py-3.5">{l.status === "pending" && <ReviewButtons onReview={review("teacher")} id={l.id} />}</td>
                </tr>
              ))}
              {teacherLeaves.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">No teacher leave applications yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {section === "student" && (
        <div>
          <form onSubmit={submitStudentLeave} className="bg-white border border-slate-200 rounded-2xl p-5 mb-4" data-testid="admin-student-leave-form">
            <p className="font-heading font-bold text-sm mb-3">Record a student leave</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <Select value={cForm.student_id} onValueChange={(v) => setCForm({ ...cForm, student_id: v })}>
                <SelectTrigger data-testid="admin-sleave-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.class_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="date" data-testid="admin-sleave-from" value={cForm.from_date} onChange={(e) => setCForm({ ...cForm, from_date: e.target.value })} required />
              <Input type="date" data-testid="admin-sleave-to" value={cForm.to_date} onChange={(e) => setCForm({ ...cForm, to_date: e.target.value })} required />
              <Input data-testid="admin-sleave-reason" placeholder="Reason" value={cForm.reason} onChange={(e) => setCForm({ ...cForm, reason: e.target.value })} required />
              <Button type="submit" data-testid="admin-sleave-submit" disabled={creating || !cForm.student_id} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                {creating ? "Saving…" : "Record leave"}
              </Button>
            </div>
          </form>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <label className="flex items-center gap-2 text-sm font-semibold"><Checkbox checked={pendingStudentLeaves.length > 0 && selectedStudentLeaves.length === pendingStudentLeaves.length} onCheckedChange={(v) => setSelectedStudentLeaves(v ? pendingStudentLeaves.map((l) => l.id) : [])} /> Select all pending</label>
            <Button size="sm" disabled={!selectedStudentLeaves.length} onClick={() => bulkReviewStudents("approved")}>Approve selected</Button>
            <Button size="sm" variant="outline" disabled={!selectedStudentLeaves.length} onClick={() => bulkReviewStudents("rejected")}>Reject selected</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
              <tr><th className="px-5 py-3">Select</th><th className="px-5 py-3">Student</th><th className="px-5 py-3">Class</th><th className="px-5 py-3">Dates</th><th className="px-5 py-3">Reason</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody>
              {studentLeaves.map((l) => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`student-leave-${l.id}`}>
                  <td className="px-5 py-3.5"><Checkbox disabled={l.status !== "pending"} checked={selectedStudentLeaves.includes(l.id)} onCheckedChange={(v) => setSelectedStudentLeaves((cur) => v ? [...new Set([...cur, l.id])] : cur.filter((id) => id !== l.id))} /></td>
                  <td className="px-5 py-3.5 font-semibold">{l.student_name}<span className="block text-xs font-normal text-slate-400">by {l.parent_name}</span></td>
                  <td className="px-5 py-3.5">{l.class_name}</td>
                  <td className="px-5 py-3.5">{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</td>
                  <td className="px-5 py-3.5 text-slate-600 max-w-xs truncate">{l.reason}</td>
                  <td className="px-5 py-3.5">{leaveBadge(l.status)}{l.reviewed_by && <span className="block text-xs text-slate-400 mt-1">Reviewed by {l.reviewed_by}</span>}</td>
                  <td className="px-5 py-3.5">{l.status === "pending" && <ReviewButtons onReview={review("student")} id={l.id} />}</td>
                </tr>
              ))}
              {studentLeaves.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400">No student leave applications yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function StaffAttendanceTab() {
  const [date, setDate] = useState(todayLocal());
  const [staff, setStaff] = useState([]);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");

  useEffect(() => {
    api.get("/school/staff-attendance", { params: { date } })
      .then((r) => {
        setStaff(r.data.staff);
        const m = {};
        r.data.staff.forEach((s) => (m[s.id] = s.status));
        setMarks(m);
        setDirty(false);
      })
      .catch((e) => toast.error(formatErr(e)));
  }, [date]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/school/staff-attendance", { date, marks: Object.entries(marks).map(([staff_id, status]) => ({ staff_id, status })) });
      toast.success("Staff attendance saved");
      setDirty(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const opts = [
    ["present", "bg-status-green text-white", "bg-status-green/15 text-status-green"],
    ["absent", "bg-status-red text-white", "bg-status-red/10 text-status-red"],
    ["leave", "bg-status-amber text-amber-900", "bg-status-amber/20 text-amber-700"],
    ["half_day", "bg-status-amber text-amber-900", "bg-status-amber/20 text-amber-700"],
    ["late", "bg-status-amber text-amber-900", "bg-status-amber/20 text-amber-700"],
    ["on_duty", "bg-status-blue text-white", "bg-status-blue/10 text-status-blue"],
  ];

  return (
    <div data-testid="staff-attendance-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Staff Attendance</h1>
          <p className="text-sm text-slate-500 mt-1">Daily staff register — Present, Absent, Leave, Half Day, Late or On Duty.</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="date" data-testid="staff-attendance-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 bg-white" />
          <Button data-testid="staff-attendance-save-btn" onClick={save} disabled={saving || !dirty} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
            {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </div>
      <div className="mb-4 max-w-sm">
        <Input data-testid="staff-search-input" placeholder="Search staff by name…" value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} className="bg-white" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
            <tr><th className="px-5 py-3">Staff member</th><th className="px-5 py-3">Status</th></tr>
          </thead>
          <tbody>
            {staff.filter((s) => s.name.toLowerCase().includes(staffSearch.toLowerCase())).map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`staff-row-${s.id}`}>
                <td className="px-5 py-3.5">
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.email}</p>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-2">
                    {opts.map(([val, activeCls, idleCls]) => (
                      <button
                        key={val}
                        data-testid={`staff-mark-${s.id}-${val}`}
                        onClick={() => { setMarks({ ...marks, [s.id]: val }); setDirty(true); }}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wide transition-all duration-200 active:scale-95 ${marks[s.id] === val ? activeCls : idleCls}`}
                      >
                        {val.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {staff.length === 0 && <tr><td colSpan={2} className="px-5 py-10 text-center text-sm text-slate-400">No staff members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function VisitorsTab() {
  const [visitors, setVisitors] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", aadhaar: "", purpose: "", visitor_type: "parent",
    visit_date: todayLocal(), visit_time: new Date().toTimeString().slice(0, 5),
  });

  const load = (q = search) => {
    api.get("/school/visitors", { params: { search: q || undefined } })
      .then((r) => setVisitors(r.data))
      .catch((e) => toast.error(formatErr(e)));
  };
  useEffect(() => load(""), []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/school/visitors", form);
      toast.success("Visitor logged");
      setForm({ name: "", mobile: "", aadhaar: "", purpose: "", visitor_type: "parent", visit_date: todayLocal(), visit_time: new Date().toTimeString().slice(0, 5) });
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const typeColor = { parent: "bg-brand-mint/15 text-teal-700", vendor: "bg-status-amber/20 text-amber-700", guest: "bg-status-blue/10 text-status-blue", official: "bg-brand-coral/10 text-brand-coralDark", other: "bg-slate-100 text-slate-600" };

  return (
    <div data-testid="visitors-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-6">Visitor Management</h1>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="visitor-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div><Label htmlFor="visitor-name">Name</Label><Input id="visitor-name" data-testid="visitor-name-input" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label htmlFor="visitor-mobile">Mobile</Label><Input id="visitor-mobile" data-testid="visitor-mobile-input" required className="mt-1.5" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 …" /></div>
          <div><Label htmlFor="visitor-aadhaar">Aadhaar (optional)</Label><Input id="visitor-aadhaar" data-testid="visitor-aadhaar-input" className="mt-1.5" maxLength={12} value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value.replace(/\D/g, "") })} placeholder="XXXX XXXX XXXX" /></div>
          <div>
            <Label>Visitor type</Label>
            <Select value={form.visitor_type} onValueChange={(v) => setForm({ ...form, visitor_type: v })}>
              <SelectTrigger data-testid="visitor-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="guest">Guest</SelectItem>
                <SelectItem value="official">Official</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label htmlFor="visitor-purpose">Purpose</Label><Input id="visitor-purpose" data-testid="visitor-purpose-input" required className="mt-1.5" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Parent-teacher meeting" /></div>
          <div><Label htmlFor="visitor-date">Date</Label><Input id="visitor-date" data-testid="visitor-date-input" type="date" required className="mt-1.5" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} /></div>
          <div><Label htmlFor="visitor-time">Time</Label><Input id="visitor-time" data-testid="visitor-time-input" type="time" required className="mt-1.5" value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} /></div>
        </div>
        <Button type="submit" data-testid="visitor-submit-btn" disabled={saving} className="mt-4 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 active:scale-95">
          <Plus size={16} className="mr-1.5" /> {saving ? "Logging…" : "Log visitor"}
        </Button>
      </form>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="visitor-search-input" placeholder="Search name or mobile…" className="pl-9 bg-white" value={search} onChange={(e) => { setSearch(e.target.value); load(e.target.value); }} />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
            <tr><th className="px-5 py-3">Visitor</th><th className="px-5 py-3">Mobile</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Purpose</th><th className="px-5 py-3">Date & time</th></tr>
          </thead>
          <tbody>
            {visitors.map((v) => (
              <tr key={v.id} className="border-t border-slate-100" data-testid={`visitor-row-${v.id}`}>
                <td className="px-5 py-3.5 font-semibold">{v.name}{v.aadhaar && <span className="block text-xs font-normal text-slate-400">Aadhaar ••••{v.aadhaar.slice(-4)}</span>}</td>
                <td className="px-5 py-3.5">{v.mobile}</td>
                <td className="px-5 py-3.5"><span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${typeColor[v.visitor_type] || typeColor.other}`}>{v.visitor_type}</span></td>
                <td className="px-5 py-3.5 text-slate-600 max-w-xs truncate">{v.purpose}</td>
                <td className="px-5 py-3.5">{fmtDate(v.visit_date)} · {v.visit_time}</td>
              </tr>
            ))}
            {visitors.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-400">No visitors logged yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ExamsTab({ classes }) {
  const [exams, setExams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", class_id: "", subject: "", exam_date: todayLocal(), max_marks: "", term: "Term 1" });

  const load = () => {
    api.get("/school/exams").then((r) => setExams(r.data)).catch((e) => toast.error(formatErr(e)));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/school/exams", { ...form, max_marks: Number(form.max_marks) });
      toast.success("Exam created — teachers can now enter marks");
      setForm({ name: "", class_id: "", subject: "", exam_date: todayLocal(), max_marks: "", term: "Term 1" });
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/school/exams/${id}`);
      toast.success("Exam deleted");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div data-testid="exams-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-6">Exams</h1>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="exam-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div><Label htmlFor="exam-name">Exam name</Label><Input id="exam-name" data-testid="exam-name-input" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Unit Test 1" /></div>
          <div>
            <Label>Class</Label>
            <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })} required>
              <SelectTrigger data-testid="exam-class-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="exam-subject">Subject</Label><Input id="exam-subject" data-testid="exam-subject-input" required className="mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Maths" /></div>
          <div><Label htmlFor="exam-date">Date</Label><Input id="exam-date" data-testid="exam-date-input" type="date" required className="mt-1.5" value={form.exam_date} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} /></div>
          <div><Label htmlFor="exam-marks">Max marks</Label><Input id="exam-marks" data-testid="exam-marks-input" type="number" min="1" required className="mt-1.5" value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: e.target.value })} placeholder="50" /></div>
          <div>
            <Label>Term</Label>
            <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}>
              <SelectTrigger data-testid="exam-term-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Term 1">Term 1</SelectItem>
                <SelectItem value="Term 2">Term 2</SelectItem>
                <SelectItem value="Term 3">Term 3</SelectItem>
                <SelectItem value="Final">Final</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="submit" data-testid="exam-submit-btn" disabled={saving} className="mt-4 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 active:scale-95">
          <Plus size={16} className="mr-1.5" /> {saving ? "Creating…" : "Create exam"}
        </Button>
      </form>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {exams.map((e) => (
          <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] transition-all duration-300" data-testid={`exam-card-${e.id}`}>
            <div className="flex items-start justify-between">
              <p className="font-heading font-bold">{e.name}</p>
              <button data-testid={`delete-exam-${e.id}`} onClick={() => remove(e.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete exam"><Trash size={15} /></button>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{e.subject} · {e.class_name} · {e.term || "Term 1"}</p>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-600">{fmtDate(e.exam_date)} · /{e.max_marks} marks</span>
              <span className="font-bold text-brand-coralDark">{e.marks_entered} graded</span>
            </div>
          </div>
        ))}
        {exams.length === 0 && <p className="text-sm text-slate-400 py-6">No exams yet — create the first one above.</p>}
      </div>
    </div>
  );
}

export function ReportsTab({ classes }) {
  const [mode, setMode] = useState("daily");
  const [daily, setDaily] = useState({ from: daysAgoLocal(7), to: todayLocal(), class_id: "", search: "" });
  const [dailyRows, setDailyRows] = useState(null);
  const [monthly, setMonthly] = useState({ month: todayLocal().slice(0, 7), class_id: "" });
  const [monthlyRows, setMonthlyRows] = useState(null);
  const [pf, setPf] = useState({ search: "", class_id: "" });
  const [pfRows, setPfRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDaily = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/school/reports/attendance/range", { params: { from: daily.from, to: daily.to, class_id: daily.class_id || undefined, search: daily.search || undefined } });
      setDailyRows(data.rows);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  };

  const downloadReport = async (fmt) => {
    const params =
      mode === "daily"
        ? { kind: "range", from: daily.from, to: daily.to, class_id: daily.class_id || undefined, search: daily.search || undefined }
        : mode === "monthly"
        ? { kind: "monthly", month: monthly.month, class_id: monthly.class_id || undefined }
        : { kind: "portfolio", search: pf.search || undefined, class_id: pf.class_id || undefined };
    try {
      const res = await api.get("/school/reports/export", { params: { ...params, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${params.kind}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    }
  };

  const DownloadButtons = ({ testid }) => (
    <div className="flex gap-2">
      <Button variant="outline" data-testid={`${testid}-csv`} onClick={() => downloadReport("csv")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
        <DownloadSimple size={15} className="mr-1" /> CSV
      </Button>
      <Button variant="outline" data-testid={`${testid}-excel`} onClick={() => downloadReport("xlsx")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
        <DownloadSimple size={15} className="mr-1" /> Excel
      </Button>
    </div>
  );
  const runMonthly = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/school/reports/attendance/monthly", { params: { month: monthly.month, class_id: monthly.class_id || undefined } });
      setMonthlyRows(data.rows);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  };
  const runPortfolio = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/school/reports/portfolio", { params: { search: pf.search || undefined, class_id: pf.class_id || undefined } });
      setPfRows(data.rows);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  };

  useEffect(() => { runDaily(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ClassSelect = ({ value, onChange, testid }) => (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="w-40 bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All classes</SelectItem>
        {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="reports-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-extrabold">Reports</h1>
        <div className="flex gap-2">
          {[["daily", "Daily attendance"], ["monthly", "Monthly attendance"], ["portfolio", "Student portfolio"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`report-mode-${id}`}
              onClick={() => setMode(id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors duration-200 ${mode === id ? "bg-brand-coral text-white" : "bg-white border border-slate-200 text-slate-600"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {mode === "daily" && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input type="date" data-testid="daily-report-from" value={daily.from} onChange={(e) => setDaily({ ...daily, from: e.target.value })} className="w-40 bg-white" />
            <span className="text-slate-400 text-sm">to</span>
            <Input type="date" data-testid="daily-report-to" value={daily.to} onChange={(e) => setDaily({ ...daily, to: e.target.value })} className="w-40 bg-white" />
            <ClassSelect value={daily.class_id} onChange={(v) => setDaily({ ...daily, class_id: v })} testid="daily-report-class" />
            <Input data-testid="daily-report-search" placeholder="Search student…" value={daily.search} onChange={(e) => setDaily({ ...daily, search: e.target.value })} className="w-48 bg-white" />
            <Button data-testid="daily-report-run" onClick={runDaily} disabled={loading} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">Run report</Button>
            <DownloadButtons testid="daily-download" />
          </div>
          {dailyRows && (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="daily-report-table">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
                  <tr><th className="px-5 py-3">Date</th><th className="px-5 py-3">Student</th><th className="px-5 py-3">Roll</th><th className="px-5 py-3">Class</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Marked at</th></tr>
                </thead>
                <tbody>
                  {dailyRows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-5 py-3 text-slate-500">{r.date}</td>
                      <td className="px-5 py-3 font-semibold">{r.student}</td>
                      <td className="px-5 py-3">{r.roll_no || "—"}</td>
                      <td className="px-5 py-3">{r.class}</td>
                      <td className="px-5 py-3"><span className={`text-xs font-extrabold px-2.5 py-1 rounded-full uppercase ${r.status === "present" ? "bg-status-green/15 text-status-green" : "bg-status-red/10 text-status-red"}`}>{r.status}</span></td>
                      <td className="px-5 py-3 text-slate-500">{r.marked_at}</td>
                    </tr>
                  ))}
                  {dailyRows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">No attendance records for this range.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {mode === "monthly" && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input type="month" data-testid="monthly-report-month" value={monthly.month} onChange={(e) => setMonthly({ ...monthly, month: e.target.value })} className="w-44 bg-white" />
            <ClassSelect value={monthly.class_id} onChange={(v) => setMonthly({ ...monthly, class_id: v })} testid="monthly-report-class" />
            <Button data-testid="monthly-report-run" onClick={runMonthly} disabled={loading} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">Run report</Button>
            <DownloadButtons testid="monthly-download" />
          </div>
          {monthlyRows && (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="monthly-report-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
                  <tr><th className="px-5 py-3">Student</th><th className="px-5 py-3">Class</th><th className="px-5 py-3">Present</th><th className="px-5 py-3">Absent</th><th className="px-5 py-3">Attendance</th></tr>
                </thead>
                <tbody>
                  {monthlyRows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-semibold">{r.student}</td>
                      <td className="px-5 py-3">{r.class}</td>
                      <td className="px-5 py-3 text-status-green font-semibold">{r.present}</td>
                      <td className="px-5 py-3 text-status-red font-semibold">{r.absent}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-status-green rounded-full" style={{ width: `${r.attendance_pct}%` }} />
                          </div>
                          <span className="text-xs font-bold">{r.attendance_pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {monthlyRows.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-400">No records for this month.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {mode === "portfolio" && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input data-testid="portfolio-search" placeholder="Search by name…" value={pf.search} onChange={(e) => setPf({ ...pf, search: e.target.value })} className="w-48 bg-white" />
            <ClassSelect value={pf.class_id} onChange={(v) => setPf({ ...pf, class_id: v })} testid="portfolio-class" />
            <Button data-testid="portfolio-run" onClick={runPortfolio} disabled={loading} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">Run report</Button>
            <DownloadButtons testid="portfolio-download" />
          </div>
          {pfRows && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="portfolio-results">
              {pfRows.map((r) => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`portfolio-card-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-heading font-bold">{r.student}</p>
                      <p className="text-xs text-slate-500">{r.class}{r.roll_no ? ` · Roll ${r.roll_no}` : ""}</p>
                    </div>
                    {r.attendance_pct_30d !== null && (
                      <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${r.attendance_pct_30d >= 75 ? "bg-status-green/15 text-status-green" : "bg-status-red/10 text-status-red"}`}>
                        {r.attendance_pct_30d}% (30d)
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="bg-[#F4F5F7] rounded-xl p-2.5"><p className="font-heading font-extrabold">{inr(r.fees_invoiced)}</p><p className="text-[10px] text-slate-500">Invoiced</p></div>
                    <div className="bg-status-green/10 rounded-xl p-2.5"><p className="font-heading font-extrabold text-status-green">{inr(r.fees_paid)}</p><p className="text-[10px] text-slate-500">Paid</p></div>
                    <div className="bg-status-red/10 rounded-xl p-2.5"><p className="font-heading font-extrabold text-status-red">{inr(r.fees_due)}</p><p className="text-[10px] text-slate-500">Due</p></div>
                  </div>
                  {r.exams.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {r.exams.map((e, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-[#F4F5F7] rounded-lg px-3 py-1.5">
                          <span className="font-medium">{e.exam} · {e.subject}</span>
                          <span className="font-bold">{e.marks}/{e.max_marks}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {pfRows.length === 0 && <p className="text-sm text-slate-400 py-6">No students match the search.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
